import { nameDataset } from '@/entities/dataset';
import type { CatalogueNames, DatasetNaming } from '@/entities/dataset';
import { subjectIsDataset } from '@/features/incidents';
import type { BadgeTone } from '@/shared/ui/StatusBadge';

/* The arithmetic behind the dashboard, separated from the rendering so it is testable without a
 * browser and so two cards cannot disagree about what "needs attention" means.
 */

export interface SeverityCounts {
  readonly critical: number;
  readonly warning: number;
  readonly info: number;
}

/** Incident severities, counted. The vocabulary is `IncidentSeverity`'s, not an invented one. */
export function countSeverities(
  incidents: readonly { readonly severity: string }[],
): SeverityCounts {
  let critical = 0;
  let warning = 0;
  let info = 0;
  for (const incident of incidents) {
    if (incident.severity === 'critical') critical += 1;
    else if (incident.severity === 'warning') warning += 1;
    else if (incident.severity === 'info') info += 1;
  }
  return { critical, warning, info };
}

/* What the headline "needs attention" figure counts, defined once.
 *
 * **Critical and warning, never info.** An info incident is a record rather than a request, and folding
 * it in would make the number that is supposed to drive somebody's morning go up when nothing
 * actionable happened — which is how a dashboard number stops being read. */
export function needsAttention(counts: SeverityCounts): number {
  return counts.critical + counts.warning;
}

/** The tone for a count of things wanting action. Zero is `ok`, and it is the only `ok`. */
export function attentionTone(counts: SeverityCounts): BadgeTone {
  if (counts.critical > 0) return 'danger';
  if (counts.warning > 0) return 'warn';
  return 'ok';
}

/* Anomaly verdicts that mean "look at this". `insufficient_history` and `undetermined` deliberately do
 * not count: the first is a cold start and the second is a series the maths did not apply to, and
 * `core.ai.anomaly` keeps all four apart precisely so an unmeasured dataset is not reported as a
 * healthy one. Counting them here would undo that in the one place a reader looks first. */
export function countAnomalous(
  findings: readonly { readonly verdict: string }[],
): number {
  return findings.filter((finding) => finding.verdict === 'anomalous').length;
}

/** How many of a deployment's capabilities are not production-grade, from `/readiness` itself. */
export interface Degradation {
  readonly count: number;
  readonly tone: BadgeTone;
}

export function degradationOf(
  degraded: readonly { readonly fidelity: string }[],
): Degradation {
  const missing = degraded.filter((entry) => entry.fidelity === 'missing').length;
  return {
    count: degraded.length,
    /* A stub is a working deployment with a placeholder behind it; a missing capability answers
     * nothing at all. So `missing` is amber and stub-only is informational — reporting every dev
     * profile as a warning is how a health indicator gets ignored. */
    tone: missing > 0 ? 'warn' : degraded.length > 0 ? 'info' : 'ok',
  };
}

/* Whether a queue is worth showing as a number or as a sentence. A queue of zero is a real, useful
 * answer — "nothing is waiting for you" — so it renders as `0` rather than being hidden. What must not
 * render as `0` is a queue that could not be *read*, which is the `null` case the cards handle
 * separately. */
export function queueTone(depth: number): BadgeTone {
  return depth === 0 ? 'ok' : 'info';
}

/* ---------------------------------------------------------------- needs attention

 * The merged worklist behind `AttentionTable`. Four sources — incidents, anomaly findings, pending
 * PII proposals and remediation plans awaiting a signature — reduced to one ordered list.
 *
 * **Merging in the model rather than in the screen is the whole point.** A steward's morning
 * question is not "are there incidents" and then "are there anomalies", it is "what is worst"; a
 * dashboard that renders four separate lists makes the reader do that merge, and the row they most
 * need is whichever sorts first across all four. Doing it here also means the ordering is testable
 * without a browser, which a JSX-embedded sort is not.
 */

/** A source that could not be read. Kept out of the rows and reported separately, because a failed
 * read must never reduce the length of a worklist — that would render as "less needs you". */
export interface AttentionSource<T> {
  readonly rows: readonly T[] | null;
  readonly label: string;
}

export type AttentionKind = 'incident' | 'anomaly' | 'review' | 'approval';

export interface AttentionItem {
  readonly id: string;
  readonly kind: AttentionKind;
  /** What the row is about: a resolved name, or a sentence saying why there is none. */
  readonly subject: string;
  /** True when `subject` is a real identifier. False when it is the explanation of a naming gap —
   * the renderer needs to know, because a sentence set in monospace reads as though the sentence
   * *were* the name. */
  readonly named: boolean;
  readonly why: string;
  /** How long it has been waiting, already humanised by the caller. */
  readonly since: string;
  /** Sorting key. Lower sorts first. */
  readonly rank: number;
  readonly tone: 'danger' | 'warn' | 'neutral';
}

/* The order things want a person in, stated as data rather than as a chain of comparisons.
 *
 * A critical incident outranks everything; an anomaly outranks a queue of proposals because it is a
 * statement that something *changed*, where a proposal is work waiting to be done. Approvals sort
 * last of the four and that is deliberate rather than dismissive: a plan awaiting a second signature
 * is blocked on a person who has already been asked, so it is the one row where nothing is
 * deteriorating while it waits. */
const RANK: Readonly<Record<AttentionKind, number>> = {
  incident: 0,
  anomaly: 1,
  review: 2,
  approval: 3,
};

/** Rank within incidents, so a critical one beats a warning from the same source. */
export function severityRank(severity: string): number {
  if (severity === 'critical') return 0;
  if (severity === 'warning') return 1;
  return 2;
}

/* Highest first, then oldest first within a rank.
 *
 * **A stable sort, and the tiebreaker is not cosmetic.** Two rows of equal rank must not swap places
 * between renders — a worklist whose order changes under the reader is one they cannot work down.
 * `Array.prototype.sort` is stable per spec, so the caller's own order carries through; the explicit
 * `id` comparison is what makes it stable across *sources* too, where the input order is whichever
 * of four requests resolved first. */
export function orderAttention(
  items: readonly AttentionItem[],
): readonly AttentionItem[] {
  return [...items].sort(
    (a, b) => RANK[a.kind] - RANK[b.kind] || a.rank - b.rank || a.id.localeCompare(b.id),
  );
}

/** How many sources failed. The screen reports this rather than silently showing a shorter list. */
export function unreadableSources(
  flags: readonly { readonly failed: boolean; readonly label: string }[],
): readonly string[] {
  return flags.filter((flag) => flag.failed).map((flag) => flag.label);
}

/* ---------------------------------------------------------------- age, for a reader

 * Whole units, largest that fits, because a worklist is scanned rather than read: "3 d" and "12 min"
 * are comparable at a glance where "4,382 minutes" is not.
 *
 * **Returns a key and a count rather than a sentence**, so the wording stays in the copy catalogue
 * and pluralisation stays ICU's job. Building the string here would put English in a `.ts` file,
 * which this codebase forbids for exactly the reason it matters on this screen: the unit words are
 * the parts most likely to need translating.
 *
 * `null` minutes - a clock skew that puts the onset in the future, or an unparseable timestamp -
 * reports `unknown` rather than "0 min". "Just now" would be a claim, and this has none to make;
 * `ageInMinutes` already refuses to invent one and this must not undo that.
 */
export interface Age {
  readonly unit: 'unknown' | 'minutes' | 'hours' | 'days';
  readonly count: number;
}

const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = 60 * 24;

export function describeAge(minutes: number | null): Age {
  if (minutes === null) return { unit: 'unknown', count: 0 };
  if (minutes >= MINUTES_PER_DAY) {
    return { unit: 'days', count: Math.floor(minutes / MINUTES_PER_DAY) };
  }
  if (minutes >= MINUTES_PER_HOUR) {
    return { unit: 'hours', count: Math.floor(minutes / MINUTES_PER_HOUR) };
  }
  return { unit: 'minutes', count: minutes };
}

/* ---------------------------------------------------------------- naming a subject

 * An incident's `subject_id` is a dataset for the four monitor kinds and a **contract** for
 * `contract_breach` - the id's referent depends on `kind`, which `Incident.subject_id`'s own
 * docstring states. So a dataset-name lookup is only meaningful for the monitor kinds, and running
 * it for a contract breach would report "this dataset is not in the catalogue" about something that
 * was never a dataset.
 *
 * That is the branching the design spec demands ("never a raw UUID") and the reason this is a
 * function rather than a map lookup at the call site. The bare-UUID defect is recorded four times in
 * this codebase's own history; rendering one on the dashboard would be the fifth, on the first screen
 * anybody opens.
 */
/* The naming outcome. Re-exported from `entities/dataset` rather than declared again: the incident
 * queue answers the same question and a second shape here would let the two drift about what a gap
 * is. What stays local is the *incident-kind* branch below, which is this screen's own knowledge.
 */
export type SubjectNaming = DatasetNaming;

/* Which kinds have a dataset subject is `features/incidents`' knowledge, not this screen's — it is a
 * fact about the incident vocabulary, and the notification feed needs the same answer. The set used
 * to live here privately; a second copy in the header would have been the two-vocabularies failure
 * this codebase records at several sites, so it moved to the feature that owns the vocabulary and
 * this file reads it. */
export function nameSubject(
  incidentKind: string,
  subjectId: string,
  names: CatalogueNames | null,
): SubjectNaming {
  if (!subjectIsDataset(incidentKind)) {
    /* A contract, not a dataset. Named by its kind rather than by a failed dataset lookup - the
     * honest statement is "this is a contract we cannot name here", not "this dataset is missing". */
    return { kind: 'gap', reasonKey: 'home.attention.subjectNotADataset' };
  }
  return nameDataset(subjectId, names);
}
